import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { BetaFlags } from '@showbie-socrative/socrative-utils';

import { PaywallTouchEventAdapter } from '~analytics/index';
import { PaywallScreens } from '~analytics/types';

type CurrentRoomActivity = {
  activityInstanceId: number;
  isLive: boolean;
};
export type teacherSliceState = {
  isTeacher: boolean;
  isPro: boolean;
  teacherId: number;
  currentRoomId: number;
  betaFlags: BetaFlags | Record<string, never> | null;
  isPaywallOpen: boolean;
  isRequestInProgress: boolean;
  currentRoomActivity: CurrentRoomActivity;
};

export const initialState = (): teacherSliceState => ({
  isTeacher: true,
  isPro: !!window.is_integration,
  teacherId: 0,
  currentRoomId: 0,
  betaFlags: null,
  isPaywallOpen: false,
  isRequestInProgress: false,
  currentRoomActivity: { activityInstanceId: 0, isLive: false },
});

const teacherSlice = createSlice({
  name: 'teacherData',
  initialState: initialState(),
  reducers: {
    setIsTeacher(state, action: PayloadAction<{ isTeacher: boolean }>) {
      const { isTeacher } = action.payload;
      state.isTeacher = isTeacher;
      return state;
    },
    setIsPro(state, action: PayloadAction<{ isPro: boolean }>) {
      const { isPro } = action.payload;
      state.isPro = isPro;
      return state;
    },
    setTeacherId(state, action: PayloadAction<{ teacherId: number }>) {
      const { teacherId } = action.payload;
      state.teacherId = teacherId;
      return state;
    },
    setBetaFlags(state, action: PayloadAction<{ betaFlags: any }>) {
      const { betaFlags } = action.payload;
      state.betaFlags = betaFlags;
      return state;
    },
    currentRoomChanged(state, action: PayloadAction<{ roomId: number }>) {
      const { roomId } = action.payload;
      state.currentRoomId = roomId;
      return state;
    },
    currentRoomActivityChanged(
      state,
      action: PayloadAction<{ activityInstanceId: number; isLive: boolean }>
    ) {
      state.currentRoomActivity = action.payload;
    },
    openPaywall(
      state,
      action: PayloadAction<{ paywallScreen?: PaywallScreens }>
    ) {
      state.isPaywallOpen = true;
      if (action?.payload?.paywallScreen) {
        new PaywallTouchEventAdapter(
          action?.payload?.paywallScreen
        ).sendAnalytics();
      }
      return state;
    },
    closePaywall(state) {
      state.isPaywallOpen = false;
      return state;
    },
    requestSent(state) {
      state.isRequestInProgress = true;
      return state;
    },
    requestFinished(state) {
      state.isRequestInProgress = false;
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('@@INIT', () => {
      return initialState();
    });
  },
});

const {
  setIsTeacher,
  setIsPro,
  setTeacherId,
  currentRoomChanged,
  currentRoomActivityChanged,
  setBetaFlags,
  openPaywall,
  closePaywall,
  requestSent,
  requestFinished,
} = teacherSlice.actions;

export {
  setIsTeacher,
  setIsPro,
  setTeacherId,
  currentRoomChanged,
  currentRoomActivityChanged,
  setBetaFlags,
  openPaywall,
  closePaywall,
  requestSent,
  requestFinished,
};

export default teacherSlice.reducer;
